import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch
} from '@stencil/core';
import { ChipsetDataItem, ChipsetType } from './chipset.model';
import { ChipSize, ChipVariant } from '../../jui-chip/jui-chip.model';
import {
  renderFormError,
  renderTooltipOnBottom,
  renderTooltipOnLeft
} from '../../../shared/form-controls/jui-form-control.utils';
import { IconSize, IconVariant } from '../../jui-icon/jui-icon.model';
import { JuiValidationEvents } from '../../../shared/form-controls/jui-form-control.model';

@Component({
  tag: 'jui-chipset',
  styleUrl: 'jui-chipset.scss',
  shadow: true
})
export class JuiChipset implements ComponentInterface {
  /**
   * Label to display for the chipset group input.
   */
  @Prop() label = '';

  /**
   * The form name for the chipset control.
   */
  @Prop() name = '';

  /**
   * Optional tooltip string
   */
  @Prop() tooltip: string = null;

  /**
   * Optional help string
   */
  @Prop() help: string = null;

  /**
   * Marking the chipset as disabled. Defaults to false
   */
  @Prop() disable = false;

  /**
   * Marking the chipset as required. Defaults to false
   */
  @Prop() required = false;

  /**
   * Show check icon for filter type chips. Default to true
   */
  @Prop() icon = true;

  /**
   * The type of chipset, changes behaviour and UX of the chips
   */
  @Prop() type: ChipsetType = ChipsetType.filter;

  /**
   * The size of chipset, adjusts the size of any slotted chips
   */
  @Prop() size: ChipSize = ChipSize.sm;

  /**
   * The event to trigger validation check
   */
  @Prop() validation: JuiValidationEvents = JuiValidationEvents.onLoad;

  /**
   * The size of chipset, adjusts the size of any slotted chips
   */
  @Prop() color: ChipVariant = ChipVariant.default;

  /**
   * Displays the chips in a pill shape
   */
  @Prop() pill = false;

  /**
   * Child chips will grow to fill the space
   */
  @Prop() grow = false;

  /**
   * Child chips will be displayed in a column
   */
  @Prop() vertical = false;

  /**
   * Optional data-qa string
   */
  @Prop() dataqastring: string = null;

  /**
   * Value to hold the current value for the chipset.
   */
  @Prop({ attribute: 'value', mutable: true }) value = '';

  /**
   * The errors for the chipset
   */
  @Prop() errors: boolean | { [key: string]: string };

  /**
   * The validation messages for the chipset
   */
  @Prop() messages: string | { [key: string]: string };

  /**
   * Input data items
   */
  @Prop() items: string[] | ChipsetDataItem[] = null;

  @State()
  private currentSelectedValues = [];

  private savedValueForItemsChange: string = null;

  @State()
  private currentSelectedValuesString = null;

  @State()
  private errorMessages = null;

  @State()
  private touched = false;

  @State()
  private tooltipLeft = false;

  @State()
  private tooltipLeftBottom = false;

  @State()
  private currentSelectedIndex = 0;

  @State()
  private chips: HTMLJuiChipElement[] = [];

  @Element() el: HTMLElement;

  /**
   * Output event emitted when change is fired
   */
  @Event() public juiChange: EventEmitter<string>;

  /**
   * Output event emitted when focus is fired
   */
  @Event() public juiFocus: EventEmitter<string>;

  /**
   * Output event emitted when blur is fired
   */
  @Event() public juiBlur: EventEmitter<string>;

  @Watch('value')
  valueChanged(value: any | undefined): void {
    this.setChipTabindex(value).then();
  }

  @Watch('items')
  itemsChanged(): void {
    const valueToUse = this.savedValueForItemsChange || this.value;
    const originalValue = this.value;

    if (!this.items || this.items.length === 0) {
      this.value = '';
    } else if (valueToUse) {
      const selectedFromValue = valueToUse.split(',').filter(v => v.trim() !== '');
      const availableValues = this.items.map(item => (typeof item === 'string' ? item : item.value));
      const filteredSelectedValues = selectedFromValue.filter(value => availableValues.includes(value));
      this.value = filteredSelectedValues.join(',');
    }

    this.savedValueForItemsChange = null;

    if (originalValue !== this.value) {
      this.juiChange.emit(this.value);
    }

    requestAnimationFrame(() => {
      this.init().then();
    });
  }

  /**
   * Public method to manually sync chip selection with current form value
   * Useful after external form updates (e.g., conflict resolution)
   */
  @Method()
  async syncWithFormValue(): Promise<void> {
    this.setSelectedValues();
  }

  @Listen('juiChipChecked')
  juiChipCheckedListener(evt: CustomEvent): void {
    this.handleChipEvent(evt);
    this.setSelectedIndex();
  }

  @Listen('juiChipRemoved')
  juiChipRemovedListener(evt: CustomEvent): void {
    // remove from the DOM
    this.chips.forEach(chip => {
      if (chip.value === evt.detail.value) {
        chip.remove();
      }
    });
    this.handleChipEvent(evt);
  }

  @Listen('keydown')
  async keydownHandler(evt: KeyboardEvent): Promise<void> {
    // adds a11y keyboard controls to mimic radio group for choice type chipsets
    if (this.type !== ChipsetType.choice) {
      return;
    }

    let nextChip: HTMLJuiChipElement;
    const thisChip: HTMLJuiChipElement = this.chips[this.currentSelectedIndex];

    if (evt.key === 'ArrowRight' || evt.key === 'ArrowDown') {
      nextChip =
        this.currentSelectedIndex === this.chips.length - 1 ? this.chips[0] : this.chips[this.currentSelectedIndex + 1];
      this.currentSelectedIndex =
        this.currentSelectedIndex === this.chips.length - 1 ? 0 : this.currentSelectedIndex + 1;
    }
    if (evt.key === 'ArrowLeft' || evt.key === 'ArrowUp') {
      nextChip =
        this.currentSelectedIndex === 0 ? this.chips[this.chips.length - 1] : this.chips[this.currentSelectedIndex - 1];
      this.currentSelectedIndex =
        this.currentSelectedIndex === 0 ? this.chips.length - 1 : this.currentSelectedIndex - 1;
    }
    if (nextChip && thisChip) {
      evt.preventDefault();
      await this.setChipTabindex(nextChip.value);
      nextChip.isSelected = true;
      thisChip.isSelected = false;

      // Update the value and emit change event for keyboard navigation
      this.value = nextChip.value;
      this.juiChange.emit(this.value);
    }
  }

  private async onSlotChange(): Promise<void> {
    await this.init();
  }

  private handleChipEvent(evt: CustomEvent): void {
    this.updateSelectedValues(evt);
    this.value = this.currentSelectedValuesString;
    this.juiChange.emit(this.value);
  }

  private onFocus(event: Event): void {
    this.touched = true;
    this.value = (event.target as HTMLInputElement).value;
    this.juiFocus.emit(this.value);
  }

  private onBlur(event: Event): void {
    this.value = (event.target as HTMLInputElement).value;
    this.juiBlur.emit(this.value);
  }

  private setChipTabindex = async (value): Promise<void> => {
    if (this.type !== ChipsetType.choice) {
      return;
    }
    // Get the first chip that is not disabled and the checked one
    const first = this.chips.find(chip => !chip.disable);
    const checked = this.chips.find(chip => chip.value === value && !chip.disable);

    if (!first && !checked) {
      return;
    }

    const focusable = checked || first;
    for (const chip of this.chips) {
      const tabindex = chip === focusable ? 0 : -1;
      if (chip === focusable && value) {
        await chip.setFocused();
      }
      await chip.setChipTabIndex(tabindex);
    }
  };

  private setSelectedValues(): void {
    // Clear previous selections from currentSelectedValues
    this.currentSelectedValues = [];

    // First clear all chip selections to ensure clean state
    this.chips.forEach((chip: HTMLJuiChipElement): void => {
      chip.isSelected = false;
    });

    if (this.value) {
      if (Array.isArray(this.value)) {
        this.value = this.value.join(',');
      }
      const checkedItems = this.value.split(',').filter(item => item.trim() !== '');
      if (checkedItems.length) {
        this.chips.forEach((chip: HTMLJuiChipElement): void => {
          if (checkedItems.includes(chip.value)) {
            this.type !== ChipsetType.input ? (chip.isSelected = true) : false;
            this.currentSelectedValues.push(chip.value);
          }
        });
      }
    }

    // Sync value back if needed - handle different chipset types appropriately
    if (this.type === ChipsetType.choice) {
      // For choice type (radio): if value exists but no chip was found, clear it
      if (this.value && this.currentSelectedValues.length === 0) {
        this.value = '';
      }
    } else {
      // For filter/group types: update value to match actual selections
      const actualValue = this.currentSelectedValues.join(',');
      if (this.value !== actualValue) {
        this.value = actualValue || '';
      }
    }

    // Update the string representation
    this.currentSelectedValuesString = this.currentSelectedValues.join();
    if (this.currentSelectedValuesString === '') {
      this.currentSelectedValuesString = null;
    }
  }

  private updateSelectedValues(evt: CustomEvent): void {
    if (evt.detail.checked) {
      // add to list
      const val = evt.detail.value;
      if (this.type === ChipsetType.choice) {
        this.currentSelectedValues = [val];
        // Refresh chips to ensure we have current DOM elements
        this.chips = this.getChips();
        this.chips.forEach((chip: HTMLJuiChipElement): void => {
          chip.isSelected = val === chip.value;
        });
      } else {
        // Create new array - Stencil @State doesn't detect in-place mutations
        this.currentSelectedValues = [...this.currentSelectedValues, val];
      }
    } else {
      if (this.type === ChipsetType.choice && this.required) {
        // do nothing
      } else {
        // Create new array - Stencil @State doesn't detect in-place mutations
        this.currentSelectedValues = this.currentSelectedValues.filter(v => v !== evt.detail.value);
      }
    }

    this.currentSelectedValuesString = this.currentSelectedValues.join();
    if (this.currentSelectedValuesString === '') {
      this.currentSelectedValuesString = null;
    }
  }

  private setSelectedIndex(): void {
    const activeIndex = this.chips.findIndex(x => x.value === this.value);
    this.currentSelectedIndex = activeIndex !== -1 ? activeIndex : 0;
  }

  private getChips(): HTMLJuiChipElement[] {
    if (this.items) {
      // Filter to only get jui-chip elements, excluding slot and other elements
      return Array.from(this.el.shadowRoot.querySelector('.jui-chipset').children).filter(
        child => child.tagName.toLowerCase() === 'jui-chip'
      ) as HTMLJuiChipElement[];
    }
    return Array.from(this.el.children) as HTMLJuiChipElement[];
  }

  private initChips(): void {
    this.chips.forEach((chip: HTMLJuiChipElement): void => {
      chip.single = false;
      chip.disable = !!this.disable;
      chip.pill = !!this.pill;
      chip.size = this.size;
      chip.color = this.color;

      if (this.grow && this.type !== ChipsetType.filter) {
        chip.style.flexGrow = '1';
      }

      switch (this.type) {
        case ChipsetType.filter: {
          chip.icon = this.icon;
          chip.selectable = true;
          break;
        }
        case ChipsetType.choice: {
          chip.icon = false;
          chip.isRadio = this.required;
          chip.selectable = true;
          break;
        }
        case ChipsetType.input: {
          chip.icon = false;
          chip.removable = true;
          chip.selectable = false;
          break;
        }
        case ChipsetType.group: {
          chip.icon = false;
          chip.selectable = true;
          break;
        }
      }
    });
  }

  componentDidLoad(): void {
    this.init().then();
  }

  componentWillUpdate(): void {
    if (this.items && this.value) {
      this.savedValueForItemsChange = this.value;
    }
    this.renderError();
  }

  componentDidRender(): void {
    if (this.tooltip) {
      this.renderTooltips();
    }
  }

  private async init(): Promise<void> {
    this.chips = this.getChips();
    this.initChips();
    this.setSelectedValues();
    if (this.type === ChipsetType.choice) {
      await this.setChipTabindex(this.value);
      this.setSelectedIndex();
    }
    if (this.validation === JuiValidationEvents.onLoad) {
      this.touched = true;
      this.renderError();
    }
  }

  private getChipProps() {
    const baseProps = {
      single: false,
      disable: !!this.disable,
      pill: !!this.pill,
      size: this.size,
      color: this.color
    };

    switch (this.type) {
      case ChipsetType.filter:
        return {
          ...baseProps,
          icon: this.icon,
          selectable: true
        };
      case ChipsetType.choice:
        return {
          ...baseProps,
          icon: false,
          isRadio: this.required,
          selectable: true
        };
      case ChipsetType.input:
        return {
          ...baseProps,
          icon: false,
          removable: true,
          selectable: false
        };
      case ChipsetType.group:
        return {
          ...baseProps,
          icon: false,
          selectable: true
        };
      default:
        return baseProps;
    }
  }

  private isItemsStringArray(items: string[] | ChipsetDataItem[]): items is string[] {
    return typeof items[0] === 'string';
  }

  private renderDataItems() {
    if (!this.items) {
      return null;
    }

    if (this.items.length) {
      let dataQaChipItem = `chip`;
      if (this.dataqastring) {
        dataQaChipItem = `${this.dataqastring}${dataQaChipItem.charAt(0).toUpperCase() + dataQaChipItem.slice(1)}`;
      }
      const chipProps = this.getChipProps();
      const chipStyle = this.grow && this.type !== ChipsetType.filter ? { flexGrow: '1' } : {};

      if (this.isItemsStringArray(this.items)) {
        // For string arrays, use the string as both value and display text

        return this.items.map((item, index) => {
          return (
            <jui-chip
              dataqastring={`${dataQaChipItem}--${index}`}
              key={`chip-${index}-${item}`}
              value={item}
              {...chipProps}
              style={chipStyle}
            >
              {item}
            </jui-chip>
          );
        });
      } else {
        // For ChipsetDataItem arrays, use value for tracking and label for display
        return this.items.map((item, index) => {
          return (
            <jui-chip
              dataqastring={`${dataQaChipItem}--${index}`}
              key={`chip-${index}-${item.value}`}
              value={item.value}
              {...chipProps}
              style={chipStyle}
            >
              {item.label}
            </jui-chip>
          );
        });
      }
    }

    return null;
  }

  render() {
    const errorFragment = this.errorMessages ? this.errorMessages : null;
    const dataQaTagLabel = this.dataqastring ? `${this.dataqastring}_formFieldLabel` : null;
    const dataQaTagInput = this.dataqastring ? `${this.dataqastring}_formField` : null;
    const dataItems = this.renderDataItems();
    const helpText = this.help ? <div class="jui-control__help">{this.help}</div> : null;

    const tooltip = this.tooltip ? (
      <div
        class={{
          'jui-control__tooltip': true,
          [`jui-control__tooltip--input-${this.size}`]: true
        }}
      >
        <jui-icon
          class={{ 'jui-control__tooltip-icon': true }}
          name="help_outline"
          cursor="help"
          color={IconVariant.info}
          size={IconSize.xs}
          tabindex={-1}
        ></jui-icon>
        <span
          class={{
            'jui-control__tooltip-text': true,
            'jui-control__tooltip-text--left': this.tooltipLeft && !this.tooltipLeftBottom,
            'jui-control__tooltip-text--left-bottom': this.tooltipLeftBottom
          }}
        >
          {this.tooltip}
        </span>
      </div>
    ) : null;

    return (
      <Host data-qa={this.dataqastring || null}>
        <div class="jui-control">
          {this.label ? (
            <label
              {...(dataQaTagLabel && { 'data-qa': dataQaTagLabel })}
            class={{
              'jui-control__label': true,
              'jui-control__label--has-value': !!this.value,
              'jui-control__label--disabled': !!this.disable,
              'jui-control__label--narrower': !!this.tooltip
            }}
          >
            <div
              class={{
                'jui-control__label-text': true
              }}
            >
              {this.required ? `${this.label} *` : this.label}
            </div>
            {tooltip}
          </label>
        ) : null}
        {helpText}
        <div
          role={this.type === 'choice' ? 'radiogroup' : 'group'}
          onFocusout={this.onBlur.bind(this)}
          onFocusin={this.onFocus.bind(this)}
          class={{
            'jui-chipset': true,
            [`jui-chipset--${this.size}`]: true,
            'jui-chipset--vertical': this.vertical,
            'jui-chipset--invalid': !!this.errors && this.touched
          }}
          {...(dataQaTagInput && { 'data-qa': dataQaTagInput })}
        >
          {dataItems}
          <slot onSlotchange={this.onSlotChange.bind(this)}></slot>
        </div>
        {errorFragment}
        </div>
      </Host>
    );
  }

  private renderTooltips(): void {
    this.tooltipLeft = renderTooltipOnLeft(this.el, 'jui-chipset');
    if (this.tooltipLeft) {
      this.tooltipLeftBottom = renderTooltipOnBottom(this.el, 'jui-chipset');
    }
  }

  private renderError(): void {
    this.errorMessages = renderFormError(this.errors, this.messages);
  }
}
